/**
 * The PowerPoint native (multi-hop) agent loop, shared by every provider.
 *
 * The loop owns all agent behaviour: hop limits, the per-turn tool-call
 * budget, failing closed on id-less tool calls, forced finalization at the
 * boundary, golden traces, metrics, and terminal finalization. A provider
 * session only translates turns to and from its wire format, so GPT and Claude
 * runs follow exactly the same control flow.
 */

import type {
  PresentationContent,
  PresentationModification,
  ReferenceSlideImage,
} from "../../../types";
import type { PptxLangfusePhase } from "../../../langfuseTrace";
import type { AiRequestOptions, StreamCallbacks, ToolExecutionPolicy } from "../types";
import { finalizeModelModifications } from "../modificationFinalizer";
import { recordOpenAINativeMetric } from "../openaiNativeRollout";
import {
  FUNCTION_CALL_NO_OUTPUT_ERROR,
  buildFallbackResultsForUnansweredCalls,
  captureGoldenToolTrace,
  handleNativeFunctionCalls,
  normalizeToolResultForOutput,
  type GoldenToolTraceEntry,
  type NativeFunctionCall,
  type NativeToolOutcome,
} from "./toolCalls";

export const NATIVE_TOOL_LOOP_HARD_CEILING = 50;
export const NATIVE_TOOL_LOOP_DEFAULT_AGENTIC = 24;
/** Kept under its original key so existing tester overrides still apply. */
export const NATIVE_TOOL_LOOP_LIMIT_KEY = "finster_openai_native_tool_loop_limit";
export const NATIVE_TOOL_CALL_BUDGET = 64;

export const MISSING_TOOL_CALL_ID_ERROR =
  "The AI returned a tool call without an ID, so the request stopped safely.";
export const TOOL_LOOP_EXHAUSTED_ERROR = "The AI ran out of steps before producing a final answer.";
export const UNREADABLE_RESPONSE_ERROR = "The AI response could not be read.";

export interface NativeAgentTurnRequest {
  /** 0-based tool-loop iteration that this turn's stream events belong to. */
  hop: number;
  toolChoice: "auto" | "none";
  phase: PptxLangfusePhase;
  label: string;
  retries: number;
}

export interface NativeAgentTurn {
  calls: NativeFunctionCall[];
  missingCallIdCount: number;
  reasoning: string;
}

/** One run's conversation with a single provider and model. */
export interface NativeAgentSession {
  readonly provider: "openai" | "anthropic";
  /** Prefix for request labels: `<label>`, `<label>-LOOP-n`, `<label>-FINALIZE`. */
  readonly requestLabel: string;
  /** Sends the next turn, appending the model's output to the conversation. */
  sendTurn(request: NativeAgentTurnRequest): Promise<NativeAgentTurn>;
  /** Records this hop's tool outcomes (and their screenshots) for the next turn. */
  appendToolOutcomes(
    outcomes: readonly NativeToolOutcome[],
    referenceImages: readonly ReferenceSlideImage[]
  ): void;
  /** Tool calls in the conversation that still have no result. */
  pendingCalls(): { calls: NativeFunctionCall[]; missingCallIdCount: number };
  /** The terminal `{ mode, reply }` payload of the last turn, or null. */
  terminalPayload(): Record<string, unknown> | null;
  /** The provider-native result item recorded in golden tool traces. */
  toProviderResultItem(outcome: NativeToolOutcome): Record<string, unknown>;
}

export function getNativeToolLoopLimit(): number {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(NATIVE_TOOL_LOOP_LIMIT_KEY);
      if (raw) {
        const parsed = Number.parseInt(raw, 10);
        if (Number.isFinite(parsed) && parsed >= 1) {
          return Math.min(parsed, NATIVE_TOOL_LOOP_HARD_CEILING);
        }
      }
    }
  } catch {
    // localStorage unavailable (non-browser context); fall through to defaults.
  }
  return NATIVE_TOOL_LOOP_DEFAULT_AGENTIC;
}

function emitThinking(
  session: NativeAgentSession,
  turn: NativeAgentTurn,
  callbacks: StreamCallbacks
): void {
  console.log(
    `[${session.provider.toUpperCase()}_NATIVE] reasoning emit chars=${turn.reasoning.length}`
  );
  if (turn.reasoning && callbacks.onThinking) {
    callbacks.onThinking(turn.reasoning);
  }
}

export interface RunNativeAgentParams {
  session: NativeAgentSession;
  presentationContent: PresentationContent;
  callbacks: StreamCallbacks;
  toolPolicy?: ToolExecutionPolicy;
  options: AiRequestOptions;
}

export async function runNativeAgent({
  session,
  presentationContent,
  callbacks,
  toolPolicy,
  options,
}: RunNativeAgentParams): Promise<void> {
  const metric = (name: string, details: Record<string, unknown> = {}) =>
    recordOpenAINativeMetric(name, { provider: session.provider, ...details });
  const t0 = Date.now();
  let lastHopIndex = 0;
  try {
    let turn = await session.sendTurn({
      hop: 0,
      toolChoice: "auto",
      phase: options.langfusePhase ?? "chat",
      label: session.requestLabel,
      retries: 3,
    });
    let terminatedByNoFunctionCalls = false;
    const effectiveLoopLimit = Math.max(1, getNativeToolLoopLimit());
    let totalToolCalls = 0;
    const goldenToolTraceEntries: GoldenToolTraceEntry[] = [];

    for (let loopIndex = 0; loopIndex < effectiveLoopLimit; loopIndex += 1) {
      emitThinking(session, turn, callbacks);
      if (turn.missingCallIdCount > 0) {
        metric("tool_loop_failure", {
          reason: "missing_call_id",
          stage: "loop",
          missingCallIdCount: turn.missingCallIdCount,
        });
        callbacks.onError(MISSING_TOOL_CALL_ID_ERROR);
        return;
      }
      if (turn.calls.length === 0) {
        terminatedByNoFunctionCalls = true;
        break;
      }
      totalToolCalls += turn.calls.length;

      const handledCalls = await handleNativeFunctionCalls(
        turn.calls,
        presentationContent,
        callbacks,
        toolPolicy,
        options.agentToolContext,
        loopIndex,
        goldenToolTraceEntries,
        options.langfuseContext,
        (outcome) => session.toProviderResultItem(outcome)
      );
      await captureGoldenToolTrace(options.agentToolContext, goldenToolTraceEntries);
      session.appendToolOutcomes(handledCalls.outcomes, handledCalls.referenceImages);

      lastHopIndex = loopIndex + 1;
      turn = await session.sendTurn({
        hop: loopIndex + 1,
        toolChoice: "auto",
        phase: "multi_hop_executor",
        label: `${session.requestLabel}-LOOP-${loopIndex + 1}`,
        retries: 2,
      });

      // Per-turn cost guard: bound cumulative tool executions regardless of the
      // configured loop depth. Breaking here routes to the forced-finalization
      // safety net below so the turn still resolves to a final payload.
      if (totalToolCalls >= NATIVE_TOOL_CALL_BUDGET) {
        metric("tool_loop_budget_exhausted", {
          totalToolCalls,
          budget: NATIVE_TOOL_CALL_BUDGET,
          effectiveLoopLimit,
        });
        break;
      }
    }

    let pending = session.pendingCalls();
    if (pending.missingCallIdCount > 0) {
      metric("tool_loop_failure", {
        reason: "missing_call_id",
        stage: "boundary",
        missingCallIdCount: pending.missingCallIdCount,
      });
      callbacks.onError(MISSING_TOOL_CALL_ID_ERROR);
      return;
    }

    let remainingCalls = pending.calls;
    if (remainingCalls.length > 0) {
      metric("tool_loop_boundary_finalize_attempt", {
        pendingCalls: remainingCalls.length,
        configuredLoopLimit: effectiveLoopLimit,
        effectiveLoopLimit,
      });

      // Every tool call must be answered before the model can finalize.
      const fallbackResults = buildFallbackResultsForUnansweredCalls(
        remainingCalls,
        FUNCTION_CALL_NO_OUTPUT_ERROR
      );
      callbacks.onToolResult?.(fallbackResults);
      session.appendToolOutcomes(
        remainingCalls.map((call, index) => ({
          callId: call.callId,
          tool: fallbackResults[index].tool,
          output: normalizeToolResultForOutput(fallbackResults[index]),
          isError: true,
        })),
        []
      );

      turn = await session.sendTurn({
        hop: lastHopIndex + 1,
        toolChoice: "none",
        phase: "multi_hop_executor",
        label: `${session.requestLabel}-FINALIZE`,
        retries: 2,
      });
      pending = session.pendingCalls();
      if (pending.missingCallIdCount > 0) {
        metric("tool_loop_failure", {
          reason: "missing_call_id",
          stage: "post_finalize",
          missingCallIdCount: pending.missingCallIdCount,
        });
        callbacks.onError(MISSING_TOOL_CALL_ID_ERROR);
        return;
      }
      remainingCalls = pending.calls;
    }

    if (!terminatedByNoFunctionCalls && remainingCalls.length === 0) {
      emitThinking(session, turn, callbacks);
    }
    if (remainingCalls.length > 0) {
      metric("tool_loop_failure", {
        reason: "loop_limit_exceeded",
        pendingCalls: remainingCalls.length,
      });
      callbacks.onError(TOOL_LOOP_EXHAUSTED_ERROR);
      return;
    }

    const parsedPayload = session.terminalPayload();
    if (!parsedPayload) {
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      metric("parse_failure", {
        source: "native_structured_output",
        elapsedSeconds: Number.parseFloat(elapsed),
      });
      callbacks.onError(UNREADABLE_RESPONSE_ERROR);
      return;
    }

    const finalized = finalizeModelModifications(
      parsedPayload as unknown as PresentationModification
    );
    metric("apply_diagnostics", {
      diagnostics: finalized.diagnostics?.length || 0,
    });
    callbacks.onComplete(finalized);
  } catch (error) {
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    const errMsg = error instanceof Error ? error.message : "Unknown error";
    metric("request_failure", {
      elapsedSeconds: Number.parseFloat(elapsed),
      message: errMsg,
    });
    callbacks.onError(
      `Request failed after ${elapsed}s: ${errMsg}${errMsg === "Load failed" ? " (WebKit timeout — the AI took too long to respond)" : ""}`
    );
  }
}
